import { Box, Container, Typography, Paper, Divider, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  maxWidth: '1200px !important',
  minHeight: 'auto',
}))

const DocumentLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '600px',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
}))

const Sidebar = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
  padding: theme.spacing(4),
  minWidth: '250px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  color: 'white',
}))

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4),
}))

const ReleaseCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[2],
}))

const FeatureList = styled('ul')(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  '& li': {
    marginBottom: theme.spacing(1),
  },
}))

const ReleaseVersion = styled(Chip)({
  fontWeight: 600,
  fontSize: '0.875rem',
})

type ReleaseType = 'major' | 'patch' | 'hotfix'

interface ReleaseNote {
  version: string
  date: string
  features: string[]
  releaseType?: ReleaseType
  isMajor?: boolean // Deprecated: use releaseType instead, kept for backward compatibility
}

// Release notes - update this section for each release
// Release structure:
// - Major releases: 1.0.16, 1.0.17, etc. (increment patch number) - set releaseType: 'major'
// - Hotfix/Patch releases: 1.0.16.1, 1.0.16.2, etc. (append fourth number) - set releaseType: 'hotfix' or 'patch'
const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '1.0.18.2',
    date: 'August 21, 2026',
    releaseType: 'hotfix',
    features: [
      'Cell Visualization: Fixed uploading a run data or cell data file doing nothing. The file is parsed again as expected',
    ],
  },
  {
    version: '1.0.18.1',
    date: 'August 21, 2026',
    releaseType: 'hotfix',
    features: [
      'Cell Visualization: Custom experimental variables from an uploaded run data file now appear in the plot dropdowns. Previously columns beyond the tenth were silently omitted',
    ],
  },
  {
    version: '1.0.18',
    date: 'July 21, 2026',
    releaseType: 'major',
    features: [
      'Experiment Planning: RME now supports Controller 1.8.0 with FPGA 4.4.1 models, providing more accurate yield and purity estimates. The correct model is automatically selected based on the instrument\'s controller version',
      'Cell Visualization: Editing "Sample Type" on the Runs page now correctly propagates to "Color By: Sample Type" in newly created sessions, instead of showing the original numeric value from the instrument',
      'Cell Browsing: Fixed morphology classifier probability threshold not being applied to cell search requests',
    ],
  },
  {
    version: '1.0.17',
    date: 'May 18, 2026',
    releaseType: 'major',
    features: [
      'Cell Visualization: Sessions now open in a new browser tab, preserving the user\'s current workflow in the session list',
      'Runs: Added Metabase QC dashboard link for internal Deepcell users, replacing the legacy distribution menu',
      'Runs: Removed obsolete See Class/Label/OOF Distribution menu items',
      'Fixed: Custom Column Order drag-and-drop misalignment in Embedding Density multiplot mode when toggling from single-UMAP view',
    ],
  },
  {
    version: '1.0.16.1',
    date: 'April 9, 2026',
    releaseType: 'hotfix',
    features: [
      'Cell Visualization: Fixed session search in "Project onto existing session" dropdown — partial name matching now works correctly using full-text search with keyword splitting',
    ],
  },
  {
    version: '1.0.16',
    date: 'April 1, 2026',
    features: [
      'Cell Visualization: Top Up Filtered UMAPs and Recompute - Manual workflow to add additional qualifying cells that match active filters and recompute UMAP using the updated cell set, ensuring UMAPs remain representative of the population of interest after filtering',
      'Cell Visualization: Custom Sampling by % of UMAP - New option to specify integer percentages of the UMAP per run, enabling better preservation of rare or biologically important populations and clear, predictable sampling behavior for highly imbalanced datasets',
      'Cell Visualization: Project onto Existing UMAP - When creating a new session, users can now select an existing UMAP session as the projection model, enabling direct comparison of new data against a previously computed embedding without recomputing from scratch',
      'Cell Visualization: Tune Hyperparameters Inline - New Tune Hyperparameters button (T shortcut) in the Cell Visualization toolbar lets users adjust UMAP/tSNE and Leiden clustering parameters and rerun the session without leaving the current view, with a live loading state while the new session populates',
      'Differential Features: Analysis Scope - Users can now scope differential morphology feature comparisons to specific cell subsets, focusing the analysis on a defined population rather than the full session',
      'Differential Features: Per-Group Analysis Scope - Independent scope controls per comparison group, with a toggle between "Same scope for both groups" and "Different scope per group", enabling asymmetric comparisons across biologically distinct subpopulations',
      'Chip ID Included in Run Report Export - Users can now select multiple runs and download a CSV report that includes the Chip ID used for each run, enabling easier operational tracking and improved traceability',
      'New About Page - Axon now includes an About page accessible from the main navigation, displaying the currently deployed application version, high-level release notes, product overview, and support contact information',
      'Fixed Leiden clustering color display for older Cell Visualization sessions - Older sessions using Leiden-based morphotypes now correctly render each population in its assigned color instead of displaying all cells in a single color',
      'Fixed: Color By disappears if you switch out of multi-plot comparison while in custom column order mode',
    ],
    releaseType: 'major',
  },
  {
    version: '1.0.15',
    date: 'February 10, 2026',
    features: [
      'Optional Full Cell Data in JSON Exports - Users can now optionally include cellsData when exporting Cell Visualization Sessions to JSON, including full per-cell attributes and all 64 deep learning embedding dimensions',
      'Embedding Comparison Interaction Fixes - Column selection now aligns correctly with visual layout, drag-and-drop reordering behaves predictably, and eliminates offset selection issues and dead zones',
      'Analytics Improvements - Cleaner page titles and paths for Cell Visualization workflows, improved tracking of session creation parameters, and added tracking for experiment planning and exports',
      'Fixed merged morphotype visibility issue where they appeared grayed out after session reload, preventing viewing population percentages and exporting representative images',
      'Resolved JavaScript errors that caused session save failures and JSON export failures',
      'Fixed terminal failure handling in Create Visualization Session workflows',
      'Fixed manual filtering behavior for empty morphotypes',
    ],
    releaseType: 'major',
  },
  // Examples:
  // Major release (e.g., 1.0.16, 1.0.17):
  // {
  //   version: '1.0.17',
  //   date: '2025-01-15',
  //   features: [
  //     'Major feature: Enhanced cell visualization capabilities',
  //     'New feature: Advanced filtering options',
  //   ],
  //   releaseType: 'major',
  // },
  // Hotfix/Patch release (e.g., 1.0.16.1, 1.0.16.2):
  // {
  //   version: '1.0.16.1',
  //   date: '2025-01-08',
  //   features: [
  //     'Fixed critical bug in data export',
  //     'Resolved memory leak in visualization component',
  //   ],
  //   releaseType: 'hotfix',
  // },
]

// Get version from Vite environment variable
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'Unknown'

function AboutPage(): JSX.Element {
  // Helper to determine if a release is major (for backward compatibility)
  const isMajorRelease = (note: ReleaseNote): boolean => {
    return note.releaseType === 'major' || note.isMajor === true
  }

  const allReleases = RELEASE_NOTES.sort((a, b) => {
    // Simple version comparison - for more complex needs, use semver
    return b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' })
  })

  const getReleaseTypeLabel = (releaseType?: ReleaseType): string => {
    switch (releaseType) {
      case 'major':
        return 'Major Release'
      case 'patch':
        return 'Patch Release'
      case 'hotfix':
        return 'Hotfix'
      default:
        return ''
    }
  }

  const getReleaseTypeColor = (
    releaseType?: ReleaseType
  ): 'primary' | 'default' | 'warning' | 'error' => {
    switch (releaseType) {
      case 'major':
        return 'primary'
      case 'patch':
        return 'default'
      case 'hotfix':
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <StyledContainer>
        {/* Document-style Layout */}
        <DocumentLayout>
          {/* Left Sidebar with Gradient */}
          <Sidebar>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: 'white',
                  fontSize: '1.75rem',
                }}
              >
                Axon Data Suite
              </Typography>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'white',
                  fontSize: '1.25rem',
                }}
              >
                Software Release Notes
              </Typography>
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                  Current Version
                </Typography>
                <ReleaseVersion
                  label={`v${APP_VERSION}`}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1rem',
                    padding: '8px 16px',
                  }}
                />
              </Box>
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}
              >
                Deepcell
              </Typography>
            </Box>
          </Sidebar>

          {/* Right Content Area */}
          <ContentArea>
            {/* Product Overview */}
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
                fontSize: '1.25rem',
              }}
            >
              About Axon Data Suite
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.7,
                color: 'text.primary',
              }}
            >
              Axon Data Suite is a comprehensive web application for analyzing and visualizing
              high-dimensional morphology data from cell images. The platform enables researchers to
              explore cell populations through interactive visualizations, perform comparative
              analytics, and plan experiments with advanced tools.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                lineHeight: 1.7,
                color: 'text.primary',
              }}
            >
              Key capabilities include interactive morphology UMAPs, population comparative
              analytics, cell image visualizations, experiment planning tools, and classifier
              workflows that make sorting and analysis workflows faster, more reliable, and easier
              to use.
            </Typography>

            {/* Release History Section */}
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: 'text.primary',
                  fontSize: '1.5rem',
                }}
              >
                Release History
              </Typography>
              <Divider sx={{ mb: 3 }} />
              {allReleases.map((release) => (
                <ReleaseCard key={release.version} elevation={0}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <ReleaseVersion
                      label={`v${release.version}`}
                      sx={{
                        backgroundColor: isMajorRelease(release)
                          ? 'primary.main'
                          : 'action.selected',
                        color: isMajorRelease(release) ? 'white' : 'text.primary',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        padding: '4px 12px',
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {release.date}
                    </Typography>
                    {release.releaseType && release.releaseType !== 'patch' && (
                      <Chip
                        label={getReleaseTypeLabel(release.releaseType)}
                        size="small"
                        color={getReleaseTypeColor(release.releaseType)}
                      />
                    )}
                  </Box>
                  {release.features.length > 0 && (
                    <FeatureList>
                      {release.features.map((feature) => (
                        <li key={`${release.version}-${feature.substring(0, 50)}`}>
                          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            {feature}
                          </Typography>
                        </li>
                      ))}
                    </FeatureList>
                  )}
                </ReleaseCard>
              ))}
            </Box>

            {/* Closing Message */}
            <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'text.primary' }}>
                We welcome all feedback regarding Axon Data Suite and are committed to continuously
                improving the platform to better serve your research needs.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                Please contact our technical support team at{' '}
                <Typography
                  component="a"
                  href="mailto:support@deepcellbio.com"
                  variant="body1"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  support@deepcellbio.com
                </Typography>{' '}
                if you have further questions.
              </Typography>
            </Box>
          </ContentArea>
        </DocumentLayout>
      </StyledContainer>
    </Box>
  )
}

export default AboutPage
